import type { ProviderFile } from "fileParsing/types";
import { suggestDate } from "./transform";
import type { ArrayType, ObjectType, ScalarType, SupposedUsage, TypeSchema, UnionType } from "./types";

// #region Create
export const createSchema = (key: string | undefined, data: unknown, files: ProviderFile[]): UnionType => {
  if (typeof data === "boolean") {
    return [{ type: "boolean" }];
  } else if (typeof data === "string" || typeof data === "number") {
    return [
      {
        type: typeof data as "string" | "number",
        supposedUsage: findSupposedUsage(key, data, files),
      },
      ...(data === 0 || data === "" ? [{ type: "empty" as const }] : []),
    ];
  } else if (data === null || data === undefined) {
    return [{ type: "empty" }];
  } else if (Array.isArray(data)) {
    return mergeWithRealData(key, data, [{ type: "array", anyOf: [] as TypeSchema[] }], files);
  } else if (data instanceof Object) {
    return [
      {
        type: "object",
        properties: Object.fromEntries(Object.entries(data).map(([k, v]) => [k, createSchema(k, v, files)])),
      },
    ];
  }
  return [];
};

/**
 * Merges the given data with the given type, creating a new type that represents the merged data and type.
 *
 * @param {string | undefined} key - The parent key.
 * @param {*} data - The data to merge with the type.
 * @param {UnionType} type - The type to merge with the data.
 * @param {Array<ProviderFile>} files - The provider files.
 * @returns {UnionType} The new union type that represents the merged data and type.
 * @throws {Error} Throws an error if the type cannot be checked.
 */
const mergeWithRealData = (
  key: string | undefined,
  data: unknown,
  type: UnionType,
  files: ProviderFile[],
): UnionType => {
  if (
    data === null ||
    data === undefined ||
    typeof data === "string" ||
    typeof data === "number" ||
    typeof data === "boolean"
  ) {
    // Data is a primitive value
    const dataType = data === null || data === undefined ? "empty" : typeof data;
    // Add primitive value to type if not already done
    const add = type.every(({ type }) => type !== dataType);
    if (add && (typeof data === "string" || typeof data === "number")) {
      return type.concat({
        type: dataType,
        supposedUsage: findSupposedUsage(key, data, files),
      } as ScalarType);
    } else if (add) {
      return type.concat({ type: dataType } as ScalarType);
    }
    return type;
  } else if (Array.isArray(data)) {
    // Data is an array
    const ixOfArray = type.findIndex(({ type }) => type === "array");
    if (ixOfArray > -1) {
      // Given type already has an array, merge/replace of-field
      // Array-elements have types
      const anyOf = data.reduce<UnionType>(
        (result, curr) => mergeWithRealData(key, curr, result, files),
        [...(type[ixOfArray] as ArrayType).anyOf],
      );
      return type.map((t, ix) => (ix === ixOfArray ? { type: "array", anyOf } : t));
    }
    // Create and add schema of array 'data' since
    // no existing array in type has been found
    return type.concat(...createSchema(key, data, files));
  } else if (data instanceof Object) {
    // Data is an object
    const ixOfObject = type.findIndex(({ type }) => type === "object");
    if (ixOfObject > -1) {
      // Given type already has an object-type
      // Create copy of (keys) of given object-type in order to keep track
      // which keys of type aren't in data -> mark them optional afterwards
      const oldProperties = { ...(type[ixOfObject] as ObjectType).properties };
      // Generate/Merge (new) properties of object-type
      // Start from a shallow copy: the original properties object belongs to
      // a schema that may be shared with other files.
      const properties = Object.entries(data).reduce<{
        [key: string]: UnionType;
      }>(
        (result, [dataKey, value]) => {
          if (dataKey in result) {
            // Seen in both type and data -> required, and merge the types.
            // Pass dataKey, not parentKey, so findSupposedUsage sees the real
            // key name (lat/lng/long detection depends on it).
            delete oldProperties[dataKey];
            return {
              ...result,
              [dataKey]: mergeWithRealData(dataKey, value, result[dataKey], files),
            };
          }
          return { ...result, [dataKey]: createSchema(dataKey, value, files) };
        },
        { ...(type[ixOfObject] as ObjectType).properties },
      );

      // Keys left in oldProperties were in the type but not in this data
      // object -> mark them optional.
      const withOptionals = Object.keys(oldProperties).reduce(
        (props, optKey) =>
          props[optKey].some(({ type }) => type === "empty")
            ? props
            : { ...props, [optKey]: [...props[optKey], { type: "empty" as const }] },
        properties,
      );

      return type.map((t, ix) => (ix === ixOfObject ? { type: "object", properties: withOptionals } : t));
    }
    // No object in given type found, create type schema from given data
    return type.concat(...createSchema(key, data, files));
  }
  throw new Error("type cannot be checked");
};

const findSupposedUsage = (
  key: string | undefined,
  data: string | number,
  files: ProviderFile[],
): SupposedUsage | undefined => {
  if (String(data).length === 0) {
    // Usage can only supposed for non-empty values
    return undefined;
  }

  if (typeof data === "string") {
    // Check uri
    if (supposeUri(data, files)) {
      return { format: "uri" };
    }
  }

  if (key && key.toLowerCase().indexOf("lat") > -1) {
    // If (parsable as) number and contains lat keyword
    if (!Number.isNaN(Number(data))) {
      return { format: "lat" };
    }
  } else if (key && (key.toLowerCase().indexOf("long") > -1 || key.toLowerCase().indexOf("lng") > -1)) {
    // If (parsable as) number and contains either long or lng keyword
    if (!Number.isNaN(Number(data))) {
      return { format: "long" };
    }
  }

  const suggestion = suggestDate(data);
  if (suggestion) {
    return { format: "time", ...suggestion };
  }

  return undefined;
};

const supposeUri = (data: unknown, files: ProviderFile[]): boolean => {
  // 1. matches URI format?
  if (typeof data !== "string") return false;
  let uri = data as string;
  // Extremely slow: if (!new RegExp(/^(\/?[-a-zA-Z0-9@:%._\+~#=\.\s]+)*$/gi).test(uri)) return false;
  uri = uri.startsWith("/") ? uri.substring(1) : uri;
  if (uri.length < 1 || uri.length > 256) return false;

  // Matches URI Format
  // 2. file exists?
  return files.some((f) => f.path === uri);
};
// #endregion

// #region Merge
export const mergeUnionTypes = (a: UnionType, b: UnionType): UnionType => {
  return Object.values(
    [...a, ...b].reduce<{ [t: string]: TypeSchema }>(
      (result, el) => ({
        ...result,
        [el.type]: result[el.type] === undefined ? el : mergeTypeSchemas(result[el.type], el),
      }),
      {},
    ),
  );
};

export const mergeTypeSchemas = (a: TypeSchema, b: TypeSchema): TypeSchema => {
  if (a.type === b.type) {
    if (a.type === "string" || a.type === "number") {
      return {
        type: a.type,
        supposedUsage: mergeSupposedUsage(a.supposedUsage, (b as ScalarType).supposedUsage),
      };
    } else if (a.type === "empty" || a.type === "boolean") {
      return a;
    } else if (a.type === "array" && b.type === "array") {
      return {
        type: "array",
        anyOf: Object.values(
          a.anyOf.concat(b.anyOf).reduce<{ [t: string]: TypeSchema }>(
            (result, el) => ({
              ...result,
              [el.type]: result[el.type] === undefined ? el : mergeTypeSchemas(result[el.type], el),
            }),
            {},
          ),
        ),
      };
    } else if (a.type === "object" && b.type === "object") {
      const aentries = Object.entries(a.properties);
      const bentries = Object.entries(b.properties);
      const optionals: [string, UnionType][] = [
        ...aentries.reduce<string[]>(
          (optionals, [k]) => (!bentries.some(([bk]) => k === bk) ? optionals.concat([k]) : optionals),
          [],
        ),
        ...bentries.reduce<string[]>(
          (optionals, [k]) => (!aentries.some(([bk]) => k === bk) ? optionals.concat([k]) : optionals),
          [],
        ),
      ].map((k) => [k, [{ type: "empty" }]]);

      return {
        type: "object",
        properties: [...aentries, ...bentries, ...optionals].reduce<{
          [t: string]: UnionType;
        }>((result, [k, v]) => {
          return {
            ...result,
            [k]: result[k] === undefined ? v : mergeUnionTypes(result[k], v),
          };
        }, {}),
      };
    }
  }

  // types are not compatible
  throw new Error("failed to build file structure type: equal type attributes do not match");
};

const mergeSupposedUsage = (a: SupposedUsage | undefined, b: SupposedUsage | undefined): SupposedUsage | undefined => {
  if (a?.format === b?.format) {
    return a;
  } else if (a === undefined || b === undefined) {
    return a ?? b;
  }
  return undefined;
};
// #endregion

// #region Compare
export const isSimilarData = (a: UnionType, b: UnionType): boolean => {
  const aData = (a[0] as ObjectType | undefined)?.properties?.data ? (a[0] as ObjectType).properties.data : a;
  const bData = (b[0] as ObjectType | undefined)?.properties?.data ? (b[0] as ObjectType).properties.data : b;

  if (aData !== undefined && bData !== undefined) {
    return isSimilarUnionType(aData, bData);
  }

  return false;
};

const isSimilarUnionType = (a: UnionType, b: UnionType): boolean => {
  const ae = a.filter((t) => t.type !== "empty");
  const be = b.filter((t) => t.type !== "empty");
  // An all-empty union carries no information: treat as compatible with anything.
  if (ae.length === 0 || be.length === 0) return true;
  if (ae.length !== be.length) return false;
  // Symmetric: every member of each side must find a partner on the other.
  return (
    ae.every((at) => be.some((bt) => isSimilarTypeSchema(at, bt))) &&
    be.every((bt) => ae.some((at) => isSimilarTypeSchema(at, bt)))
  );
};

const isSimilarTypeSchema = (a: TypeSchema, b: TypeSchema): boolean => {
  if (a.type !== b.type) return false;
  if (a.type === "array" && b.type === "array") {
    // b check obsolete since inital equality check
    // check if anyOf is equal
    if (a.anyOf.length === 0 || b.anyOf.length === 0) return true;
    if (a.anyOf.length !== b.anyOf.length) return false;
    return (
      a.anyOf.every((at) => b.anyOf.some((bt) => isSimilarTypeSchema(at, bt))) &&
      b.anyOf.every((bt) => a.anyOf.some((at) => isSimilarTypeSchema(at, bt)))
    );
  }

  if (a.type === "object" && b.type === "object") {
    // b check obsolete since inital equality check
    // check if properties is equal
    if (a.properties === undefined || b.properties === undefined) {
      // TODO SR: add patternproperties
      //a.patternProperties;
      return false;
    }
    const isRequired = (v: UnionType) => !v.some((t) => t.type === "empty");
    const ae = Object.entries(a.properties);
    const be = Object.entries(b.properties);

    // Required keys on either side must be present and compatible on the other.
    // This is an AND, not an OR: the old OR made {a} match {a,b,c} whenever
    // b had no required keys, which broke reflexivity-by-transitivity in grouping.
    const requiredOk = (left: [string, UnionType][], right: ObjectType["properties"]) =>
      left
        .filter(([, v]) => isRequired(v))
        .every(([k, v]) => right[k] !== undefined && isSimilarUnionType(v, right[k]));

    if (!requiredOk(ae, b.properties)) return false;
    if (!requiredOk(be, a.properties)) return false;

    // Optional keys may be absent, but must be compatible when present.
    const optionalOk = (left: [string, UnionType][], right: ObjectType["properties"]) =>
      left
        .filter(([, v]) => !isRequired(v))
        .every(([k, v]) => right[k] === undefined || isSimilarUnionType(v, right[k]));

    if (!optionalOk(ae, b.properties)) return false;
    if (!optionalOk(be, a.properties)) return false;

    // ponytail: structural overlap only, no weighting by key count.
    // Upgrade path: require |shared| / |union| >= threshold if grouping is too eager.
    return true;
  }

  return true;
};
// #endregion

// #region Anonymize
export const removeSamples = (schema: UnionType): UnionType => {
  const desampled = schema.map((t) => removeSamplesFromTypeSchema(t));

  // Try to only extract the schema of the data
  if (desampled.length === 1 && desampled[0].type === "object") {
    if ("data" in desampled[0].properties) {
      return desampled[0].properties.data;
    }
  }

  return desampled;
};

const removeSamplesFromTypeSchema = (schema: TypeSchema): TypeSchema => {
  switch (schema.type) {
    case "boolean":
    case "empty":
      return schema;
    case "number":
    case "string":
      if (schema.supposedUsage?.format === "time") {
        return {
          ...schema,
          supposedUsage: { ...schema.supposedUsage, sample: undefined },
        };
      }
      return schema;
    case "object":
      return {
        ...schema,
        properties: Object.entries(schema.properties).reduce(
          (properties, [k, v]) => ({
            ...properties,
            [k]: removeSamples(v),
          }),
          {},
        ),
      };
    case "array":
      return {
        ...schema,
        anyOf: schema.anyOf.map((t) => removeSamplesFromTypeSchema(t)),
      };
  }
};
// #endregion
